import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Menu } from '@headlessui/react';
import {
  ArrowPathIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  ClockIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';

const SCHEDULE_LABELS = {
  every_12_hours: 'Every 12 hours',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  manual: 'Manual refresh'
};

function formatTimeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function InsightBlockWidget({
  insightBlock,
  onEdit,
  onDelete,
  onRefresh,
  refreshKey,
  readOnly = false
}) {
  const { hasPermission } = useAuthStore();
  const canEdit = !readOnly && hasPermission('edit_dashboards');
  const canUseAI = !readOnly && hasPermission('use_ai');
  const { formatCost } = useCreditCosts();
  const refreshCostLabel = formatCost('insight_block_refresh', { showParens: false }) || '5 credits';

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownRef = useRef(null);
  const [localMarkdown, setLocalMarkdown] = useState(insightBlock?.markdownContent || '');

  useEffect(() => {
    setLocalMarkdown(insightBlock?.markdownContent || '');
  }, [insightBlock?.markdownContent]);

  // Calculate initial cooldown from lastManualRefreshAt
  useEffect(() => {
    if (!insightBlock?.lastManualRefreshAt) return;

    const elapsed = Date.now() - new Date(insightBlock.lastManualRefreshAt).getTime();
    const cooldownMs = 60 * 60 * 1000;
    const remaining = Math.max(0, Math.ceil((cooldownMs - elapsed) / 1000));

    if (remaining > 0) {
      setCooldownSeconds(remaining);
    }
  }, [insightBlock?.lastManualRefreshAt]);

  // Countdown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }

    cooldownRef.current = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [cooldownSeconds]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing || cooldownSeconds > 0) return;
    setIsRefreshing(true);

    try {
      const response = await apiHelpers.refreshInsightBlock(insightBlock._id);
      const updated = response.data?.insightBlock;
      if (updated) {
        setLocalMarkdown(updated.markdownContent || '');
        setCooldownSeconds(3600);
      }
      if (onRefresh) onRefresh(updated);
      toast.success('Insight refreshed');
    } catch (error) {
      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfterSeconds || 3600;
        setCooldownSeconds(retryAfter);
        toast.error(`Cooldown active. Try again in ${Math.ceil(retryAfter / 60)} min.`);
      } else {
        toast.error('Failed to refresh insight');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [insightBlock?._id, isRefreshing, cooldownSeconds, onRefresh]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete this insight block?')) return;
    try {
      await apiHelpers.deleteInsightBlock(insightBlock._id);
      if (onDelete) onDelete(insightBlock._id);
      toast.success('Insight block deleted');
    } catch {
      toast.error('Failed to delete insight block');
    }
  }, [insightBlock?._id, onDelete]);

  const formatCooldown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const stopPropagation = (e) => e.stopPropagation();

  const handleMenuClick = (e, callback) => {
    e.stopPropagation();
    e.preventDefault();
    callback?.();
  };

  const isGenerating = insightBlock?.status === 'generating';
  const isError = insightBlock?.status === 'error';
  const scheduleLabel = SCHEDULE_LABELS[insightBlock?.refreshSchedule?.type] || 'Manual';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="drag-handle flex items-center justify-between px-4 py-2 border-b border-surface-200 dark:border-surface-700 cursor-grab active:cursor-grabbing bg-surface-50/50 dark:bg-surface-800/50">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <LightBulbIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm font-medium text-surface-700 dark:text-surface-300 truncate">
            {insightBlock?.title || insightBlock?.prompt?.substring(0, 60) || 'Insight Block'}
            {!insightBlock?.title && (insightBlock?.prompt?.length || 0) > 60 ? '...' : ''}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {/* Refresh button */}
          {canUseAI && (
            <button
              onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              onMouseDown={stopPropagation}
              disabled={isRefreshing || cooldownSeconds > 0 || isGenerating}
              className="flex items-center gap-1 px-1.5 py-1 rounded hover:bg-surface-200 dark:hover:bg-surface-700 disabled:opacity-40 disabled:cursor-not-allowed group"
              title={cooldownSeconds > 0 ? `Refresh available in ${formatCooldown(cooldownSeconds)}` : `Refresh (${refreshCostLabel})`}
            >
              <ArrowPathIcon className={`w-4 h-4 text-surface-500 flex-shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
              {!isRefreshing && cooldownSeconds <= 0 && (
                <span className="text-xs font-medium text-surface-400 dark:text-surface-500 group-hover:text-surface-600 dark:group-hover:text-surface-300 leading-none">
                  {refreshCostLabel}
                </span>
              )}
              {cooldownSeconds > 0 && (
                <span className="text-xs font-medium text-amber-500 leading-none">
                  {formatCooldown(cooldownSeconds)}
                </span>
              )}
            </button>
          )}

          {/* Menu */}
          {canEdit && (
            <Menu as="div" className="relative" onClick={stopPropagation} onTouchStart={stopPropagation} onTouchEnd={stopPropagation}>
              <Menu.Button
                className="p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
                onTouchEnd={stopPropagation}
              >
                <EllipsisVerticalIcon className="w-4 h-4 text-surface-500" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-50 mt-1 w-40 bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 py-1">
                {canUseAI && (
                  <button
                    type="button"
                    onClick={(e) => handleMenuClick(e, () => onEdit && onEdit(insightBlock))}
                    onMouseDown={stopPropagation}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 text-left"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => handleMenuClick(e, handleDelete)}
                  onMouseDown={stopPropagation}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              </Menu.Items>
            </Menu>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-4 py-3">
        {isGenerating ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-surface-400">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Generating insight...</span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-red-500">
            <span className="text-sm font-medium">Error generating insight</span>
            <span className="text-xs text-surface-400">{insightBlock?.error}</span>
            {canUseAI && (
              <button onClick={() => onEdit && onEdit(insightBlock)} className="text-xs text-primary-500 hover:underline mt-1">
                Edit & retry
              </button>
            )}
          </div>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:my-2 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-primary-600 dark:prose-strong:text-primary-400 prose-code:bg-surface-200 dark:prose-code:bg-surface-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-surface-200 dark:prose-pre:bg-surface-900 prose-pre:text-surface-800 dark:prose-pre:text-surface-200 prose-pre:border prose-pre:border-surface-300 dark:prose-pre:border-surface-600 prose-pre:rounded-lg prose-pre:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {localMarkdown || '_No content yet._'}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-surface-200 dark:border-surface-700 text-xs text-surface-400">
        <div className="flex items-center gap-1">
          <ClockIcon className="w-3 h-3" />
          <span>{formatTimeAgo(insightBlock?.lastRefreshedAt)}</span>
        </div>
        <span>{scheduleLabel}</span>
        {cooldownSeconds > 0 && (
          <span className="text-amber-500">Refresh in {formatCooldown(cooldownSeconds)}</span>
        )}
      </div>
    </div>
  );
}
